import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useSWR from "swr";
import fetcher from "../utils/fetcher";
import { logout } from "../redux/authSlice";
import { announceRedux } from "../redux/featuresSlice";
import CartIcon from "./cartIcon";
import styles from "../styles/navbar.module.css";
import { Navbar, Offcanvas, Container } from "react-bootstrap";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";
import { FiGrid, FiHeart, FiLogOut, FiMenu, FiSearch, FiUser } from "react-icons/fi";
import logoImg from "../assets/logo-navbar.png";

const NavbarComponent = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [moveSearch, setMoveSearch] = useState(false);
  const [search, setSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef(null);
  const [menu, setMenu] = useState(false);
  const promoRef = useRef(null);
  const headerRef = useRef(null);
  const [promoHeight, setPromoHeight] = useState(0);
  
  const { data: announce } = useSWR(
    `${process.env.REACT_APP_BACKEND_URL}/api/features/announce`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      shouldRetryOnError: (err) => err.status !== 304,
      errorRetryCount: 2,
    }
  );

  useEffect(() => {
    if (announce !== undefined) {
      dispatch(announceRedux(announce));
    }
  }, [announce, dispatch]);

  // The whole header is sticky with a negative top offset equal to the promo
  // bar height, so the promo scrolls away while the nav bar stays stuck.
  useEffect(() => {
    const measure = () => setPromoHeight(promoRef.current?.offsetHeight || 0);
    measure();

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [announce]);

  // Expose the real header height as --header-h so full-screen pages
  // (login, signup) can centre their content in the space below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty("--header-h", `${el.offsetHeight}px`);
    update();

    if (!("ResizeObserver" in window)) {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleShowSearch = () => {
    setSearch(true);
    setTimeout(() => {
      setMoveSearch(true);
    }, 10);
  };

  useEffect(() => {
    if (search && inputRef.current) {
      inputRef.current.focus();
    }
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      let el = e.target;
      let searchCont = false;
      let searchIcon = false;
      let clear = false;

      // Single traversal to check both conditions
      while (el && el !== document.body) {
        if (!searchCont && el.classList.contains(styles.search)) {
          searchCont = true;
        }
        if (!searchIcon && el.classList.contains(styles.searchicon)) {
          searchIcon = true;
        }
        if (!clear && el.classList.contains(styles.clear)) {
          clear = true;
        }
        // Early exit if both are found
        if (searchCont && searchIcon && clear) break;
        el = el.parentElement;
      }

      if (search && !searchCont && !searchIcon && !clear) {
        setMoveSearch(false);
        setTimeout(() => {
          setSearch(false);
        }, 200);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      let el = e.target;
      let account = false;

      while (el && el !== document.body) {
        if (!account && el.classList.contains(styles.account)) {
          account = true;
        }

        if (account) break;
        el = el.parentElement;
      }

      if (menu && !account) {
        setMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menu]);

  const handleClear = () => {
    setSearchTerm("");
    inputRef.current.focus();
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const searchTerms = searchTerm.trim().split(/\s+/).filter(Boolean);

    if (!searchTerms?.length) return;

    const params = new URLSearchParams();
    searchTerms.forEach((term) => params.append("search", term));

    setSearch(false);
    navigate(`/products?${params.toString()}`);
    setSearchTerm("");
  };

  const handleLogout = async () => {
    setShow(false);
    await dispatch(logout()).unwrap();
    navigate("/");
  };

  return (
    <>
      <header ref={headerRef} className={styles.header} style={{ top: `-${promoHeight}px` }}>
        {announce?.announce && (
          <div className={styles.promo} ref={promoRef}>{announce.announce}</div>
        )}

        <Navbar className={styles.cont}>
        <Container fluid className={styles.nav}>
          {/* -------- Desktop -------- */}
          <div className={`center ${styles.desknav} ${styles.navcont}`}>
            <nav className={styles.navlinks}>
              <Link to="/" className={styles.links}>الرئيسية</Link>
              <Link to="/products" className={styles.links}>جميع اللوحات</Link>

              {user ? (
                <>
                  <Link to="/orders" className={styles.links}>طلباتك</Link>
                  <span
                    onClick={() => (menu ? setMenu(false) : setMenu(true))}
                    className={`cursor ${styles.account}`}
                  >
                    {user.name?.split(" ")[0]} <MdKeyboardArrowDown />
                    {menu && (
                      <div className="center">
                        <Link to="/account" className={styles.links}>حسابك</Link>
                        <button onClick={handleLogout} className="center">
                          <FiLogOut className={styles.icon} /> خروج
                        </button>
                      </div>
                    )}
                  </span>
                </>
              ) : (
                <Link to="/login" className={styles.links}>تسجيل الدخول</Link>
              )}

              {user?.isAdmin && (
                <Link to="/admin" className={styles.admin}>الأدمن</Link>
              )}
            </nav>

            <Link to="/" className={styles.logo}>
              <img src={logoImg} alt="فَذَكِّرْ" className={styles.logoImg} />
            </Link>

            <div className={`center ${styles.leftcont}`}>
              <Link to="/cart" className={styles.carticon}>
                <CartIcon />
              </Link>

              <Link to="/wishlist">
                <FiHeart className={styles.icon} />
              </Link>

              <Link to={user ? "/account" : "/login"}>
                <FiUser className={styles.icon} />
              </Link>

              <div
                onClick={handleShowSearch}
                className={`cursor ${styles.searchpill} ${styles.searchicon}`}
              >
                <FiSearch />
                <span>بحث</span>
              </div>
            </div>
          </div>

          {/* -------- Mobile -------- */}
          <div className={`center ${styles.mobnav} ${styles.navcont}`}>
            <div className={`center ${styles.rightcont}`}>
              {user ? (
                <FiMenu onClick={() => setShow(true)} className={styles.icon} />
              ) : (
                <Link to="/login">
                  <FiUser className={styles.icon} />
                </Link>
              )}

              <Link to="/products" className={styles.icon}>
                <FiGrid className={styles.icon} />
              </Link>
            </div>

            <Link to="/" className={styles.logo}>
              <img src={logoImg} alt="فَذَكِّرْ" className={styles.logoImg} />
            </Link>

            <div className={`center ${styles.leftcont}`}>
              <FiSearch onClick={handleShowSearch} className={`cursor ${styles.icon} ${styles.searchicon}`} />

              <Link to="/wishlist">
                <FiHeart className={styles.icon} />
              </Link>

              <Link to="/cart" className={styles.carticon}>
                <CartIcon />
              </Link>
            </div>
          </div>
        </Container>
        </Navbar>
      </header>

      {search && (
        <form onSubmit={handleSearch} className={moveSearch ? `${styles.search} ${styles.move}` : `${styles.search}`}>
          <input type="text" maxLength={30} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} ref={inputRef} />
          {searchTerm && (
            <MdClose className={styles.clear} onClick={handleClear} />
          )}
          <button type="submit" className="buttonNoBorder">بحث</button>
        </form>
      )}

      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement="end"
        className={styles.offcanvas}
      >
        <Offcanvas.Header>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.offcanvasbody}>
          <Link to="/" onClick={() => setShow(false)} className={styles.links}>الرئيسية</Link>
          <Link to="/orders" onClick={() => setShow(false)} className={styles.links}>طلباتك</Link>
          <Link to="/account" onClick={() => setShow(false)} className={styles.links}>حسابك</Link>

          {user?.isAdmin && (
            <Link to="/admin" onClick={() => setShow(false)} className={styles.admin}>الأدمـــن</Link>
          )}

          <button onClick={handleLogout} className="center">
            <FiLogOut className={styles.icon} /> خروج
          </button>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default NavbarComponent;
